/**
 * V123 薪资结构 API (薪资项字典 + 模板 + 员工快照).
 *
 * Endpoints:
 *   /pay-component                          薪资项字典 CRUD
 *   /pay-structure-template                 模板 CRUD + 节点管理
 *   /pay-structure-template/calculate-preview  实时拆分预览
 *   /pay-structure-template/match-by-emp       三维匹配
 *   /emp-salary-binding                     员工绑定快照 (V125-P4 rename from emp-salary-structure)
 */
import request from './request'

// ─── 薪资项 ────────────────────────────────────────────────────────

export interface PayComponent {
  id?: number
  code: string
  name: string
  category: 'BASE' | 'PERF' | 'ALLOW' | 'BONUS'
  isSocialBase?: number
  isTaxBase?: number
  isOtBase?: number
  description?: string
  enabled?: number
  // V125-P1 schema 新增 6 字段 (前端可填; 后端 entity 由后续段补全)
  taxMethod?: 'FULL' | 'QUOTA' | 'EXEMPT'
  taxQuota?: number
  isHfBase?: number
  displayIcon?: string
  displayColor?: string
  sortOrder?: number
}

export const payComponentApi = {
  list: (enabled?: number) => request.get('/pay-component', { params: enabled != null ? { enabled } : {} }),
  get: (id: number) => request.get(`/pay-component/${id}`),
  create: (data: PayComponent) => request.post('/pay-component', data),
  update: (id: number, data: Partial<PayComponent>) => request.put(`/pay-component/${id}`, data),
  delete: (id: number) => request.delete(`/pay-component/${id}`),
  toggleEnabled: (id: number) => request.post(`/pay-component/${id}/toggle`),
}

// ─── V125 计算规则库 (pay_calc_rule, 7 ruleType + XCCS017 FORMULA) ──

export type PayCalcRuleType =
  | 'FIXED'
  | 'PERCENT_OF_PACKAGE'
  | 'PERCENT_OF_PARENT'
  | 'REMAINDER'
  | 'DAILY'
  | 'CONDITIONAL'
  | 'SUM_OF_CHILDREN'
  | 'FORMULA'

export type PayCalcDailyBasis =
  | 'ATTENDANCE_DAYS'
  | 'TRIP_DAYS'
  | 'OT_DAYS'
  | 'WORKING_DAYS'

export interface PayCalcRule {
  id?: number
  code: string
  name: string
  ruleType: PayCalcRuleType
  // FIXED 用
  fixedAmount?: number
  // PERCENT_OF_* 用 (0~1)
  ratio?: number
  // DAILY 用
  dailyAmount?: number
  dailyBasis?: PayCalcDailyBasis
  // CONDITIONAL 用 (JSON 字符串, UI 解析编辑)
  conditionJson?: string
  tierJson?: string
  // XCCS017 (V256) FORMULA 用: 表达式 (变量 = pay_component.code + 系统变量)
  formulaExpr?: string
  // 上下限 + 单月封顶 (通用)
  minAmount?: number
  maxAmount?: number
  monthlyCap?: number
  // 元信息
  description?: string
  enabled?: number
}

export const payCalcRuleApi = {
  list: (params?: { enabled?: number; ruleType?: PayCalcRuleType }) =>
    request.get<any, PayCalcRule[]>('/pay-calc-rule', { params: params || {} }),
  get: (id: number) => request.get<any, PayCalcRule>(`/pay-calc-rule/${id}`),
  create: (data: PayCalcRule) => request.post<any, PayCalcRule>('/pay-calc-rule', data),
  update: (id: number, data: Partial<PayCalcRule>) =>
    request.put<any, PayCalcRule>(`/pay-calc-rule/${id}`, data),
  delete: (id: number) => request.delete(`/pay-calc-rule/${id}`),
  toggleEnabled: (id: number) => request.post<any, PayCalcRule>(`/pay-calc-rule/${id}/toggle`),
}

// ─── 模板 ──────────────────────────────────────────────────────────

export interface PayStructureTemplate {
  id?: number
  code: string
  name: string
  applicablePositionCodes?: string
  applicableLevelCodes?: string
  applicableRegionCode?: string
  isDefault?: number
  enabled?: number
  /** XCCS017 (V256): DRAFT | PUBLISHED | DISABLED (V125 遗留 ARCHIVED); 老数据可空 → 按 enabled 推断 */
  status?: string
  /** XCCS017 (V256): 版本号 (编辑发布 = 插新版本行 version+1, 老版本只读留档) */
  version?: number
  /** XCCS017 (V256): 改版链, 指向上一版 id */
  parentTemplateId?: number | null
  effectiveDate?: string
  expireDate?: string
  description?: string
}

/** XCCS017: 模板状态推断 (老数据 status 空 → enabled=1 视为已发布) */
export function templateStatusOf(t: PayStructureTemplate): 'DRAFT' | 'PUBLISHED' | 'DISABLED' | string {
  if (t.status) return t.status
  return t.enabled === 1 ? 'PUBLISHED' : 'DISABLED'
}

/**
 * XCCS017: 每 code 只留最新 PUBLISHED 版 (所有"选包下拉"统一用这个过滤 —
 * 老版本只读留档 / DISABLED 不能新套).
 */
export function latestPublishedTemplates(list: PayStructureTemplate[]): PayStructureTemplate[] {
  const byCode = new Map<string, PayStructureTemplate>()
  for (const t of list || []) {
    if (templateStatusOf(t) !== 'PUBLISHED') continue
    const prev = byCode.get(t.code)
    if (!prev || (t.version ?? 1) > (prev.version ?? 1)) byCode.set(t.code, t)
  }
  return [...byCode.values()].sort((a, b) => (a.code > b.code ? 1 : -1))
}

export interface PayStructureNode {
  id?: number
  templateId?: number
  parentId?: number | null
  componentId: number
  /** V123 fallback: 比例 (优先级低于 calcRuleId) */
  ratio?: number
  /** V123 fallback: 固定金额 (优先级低于 calcRuleId) */
  fixedAmount?: number
  /** V125 新: 关联 pay_calc_rule.id (优先) */
  calcRuleId?: number | null
  /** V125 新: 模板级别覆盖规则参数 (JSON) */
  overrideParams?: string | null
  isNegotiable?: number
  minRatio?: number
  maxRatio?: number
  displayOrder?: number
  depth?: number
}

export interface CalculatedNode {
  nodeId: number
  parentId: number | null
  depth: number
  componentCode: string
  componentName: string
  category: string
  amount: number
  ratio: number | null
  isSocialBase: boolean
  isTaxBase: boolean
  isOtBase: boolean
}

export interface CalculatePreview {
  templateId: number
  templateCode: string
  templateName: string
  packageTotal: number
  nodes: CalculatedNode[]
  socialBaseTotal: number
  taxBaseTotal: number
  otBaseTotal: number
  valid: boolean
  validationMessage: string
}

export const payStructureTemplateApi = {
  list: (params?: { enabled?: number; regionCode?: string }) =>
    request.get('/pay-structure-template', { params: params || {} }),
  get: (id: number) => request.get(`/pay-structure-template/${id}`),
  create: (data: PayStructureTemplate) => request.post('/pay-structure-template', data),
  update: (id: number, data: Partial<PayStructureTemplate>) =>
    request.put(`/pay-structure-template/${id}`, data),
  delete: (id: number) => request.delete(`/pay-structure-template/${id}`),

  listNodes: (templateId: number) =>
    request.get(`/pay-structure-template/${templateId}/nodes`),
  addNode: (templateId: number, node: PayStructureNode) =>
    request.post(`/pay-structure-template/${templateId}/node`, node),
  updateNode: (nodeId: number, patch: Partial<PayStructureNode>) =>
    request.put(`/pay-structure-template/node/${nodeId}`, patch),
  deleteNode: (nodeId: number) => request.delete(`/pay-structure-template/node/${nodeId}`),

  /** 2026-07-10: 一键去重+归一化 — 修占比≠100%/重复节点 (纯数学缩放, 不改比例语义) */
  normalize: (id: number) =>
    request.post<any, { dedupRemoved: number; levelsNormalized: number }>(
      `/pay-structure-template/${id}/normalize`,
    ),

  calculatePreview: (templateId: number, packageTotal: number) =>
    request.get('/pay-structure-template/calculate-preview', {
      params: { templateId, packageTotal },
    }),
  matchByEmp: (params: { positionCode?: string; levelCode?: string; regionCode?: string }) =>
    request.get('/pay-structure-template/match-by-emp', { params }),

  // ─── XCCS017 版本化 + 试算 ───
  /** 编辑发布 = 插新版本行 (复制节点, version+1); 老版本只读留档, 已套用员工不受影响 */
  publishNewVersion: (id: number) =>
    request.post<any, PayStructureTemplate>(`/pay-structure-template/${id}/publish-new-version`),
  /** 停用: PUBLISHED→DISABLED, 不能再新套, 存量员工 (binding 快照) 不受影响 */
  disable: (id: number) =>
    request.post<any, PayStructureTemplate>(`/pay-structure-template/${id}/disable`),
  /** 在用人数: 当前生效 binding 引用本模板版本行的员工数 */
  inUseCount: (id: number) =>
    request.get<any, number>(`/pay-structure-template/${id}/in-use-count`),
  /** 试算 (不落库): 模式 A={templateId, packageTotal} / 模式 B={components 内联}, mockVars 覆盖系统变量 */
  dryRun: (body: DryRunRequest) =>
    request.post<any, DryRunResult>('/pay-structure-template/dry-run', body),
}

// ─── XCCS017 dry-run 试算 DTO (对齐后端 salstruct/dto) ──────────────

export interface DryRunComponentInput {
  code: string
  name?: string
  calcType?: string
  calcConfig?: string
}

export interface DryRunRequest {
  /** 模式 A: 模板 id (packageTotal 必填) */
  templateId?: number
  /** 模式 B: 内联明细 (四档 FIXED/RATIO/FORMULA/INPUT) */
  components?: DryRunComponentInput[]
  packageTotal?: number
  /** 系统变量 mock: {ATT_DAYS:21.75, OT_HOURS:0, MONTH_DAYS:30, WORK_DAYS_2175:21.75} */
  mockVars?: Record<string, number>
}

export interface DryRunItem {
  code: string
  name: string
  /** 模式 A = calc_rule ruleType (FIXED/…/FORMULA) 或 V123 fallback; 模式 B = 四档 */
  calcType: string
  /** INPUT 项占位 0 */
  amount: number
  /** INPUT="核算时录入"; FORMULA=表达式回显 */
  note?: string | null
  /** 模式 A 树形层级; 模式 B 恒 null */
  depth?: number | null
  /** 模式 A 合计只加叶子; 模式 B 恒 true */
  leaf?: boolean | null
}

export interface DryRunResult {
  mode: 'TEMPLATE' | 'COMPONENTS'
  templateId?: number | null
  templateName?: string | null
  packageTotal?: number | null
  items: DryRunItem[]
  total: number
  varsUsed: Record<string, number>
}

// ─── 员工绑定快照 (V125-P4: emp_salary_structure → emp_salary_binding) ──

export interface EmpSalaryBindingSnapshot {
  id?: number
  empId: number
  templateId?: number | null
  packageTotal: number
  effectiveDate: string
  expireDate?: string
  data?: string
  sourceType?: 'ONBOARD' | 'ADJUST' | 'MIGRATE'
  sourceRefId?: number
  remark?: string
}

export interface CurrentSnapshotResp {
  snapshot: EmpSalaryBindingSnapshot | null
  nodes: CalculatedNode[]
}

/**
 * V125-P8 全员当前绑定列表行 (EmpSalaryBindingPanel 表格用).
 * 未绑定员工的 bindingId / templateId / packageTotal 等为 null.
 */
export interface EmpSalaryBindingListRow {
  empId: number
  empNo: string
  empName: string
  orgPath?: string
  position?: string
  bindingId?: number | null
  templateId?: number | null
  packageTotal?: number | null
  effectiveDate?: string | null
  sourceType?: 'ONBOARD' | 'ADJUST' | 'MIGRATE' | null
}

export interface EmpSalaryBindingListResp {
  records: EmpSalaryBindingListRow[]
  total: number
  current: number
  size: number
}

export const empSalaryBindingApi = {
  getCurrent: (empId: number) =>
    request.get('/emp-salary-binding/current', { params: { empId } }),
  listHistory: (empId: number) =>
    request.get('/emp-salary-binding/history', { params: { empId } }),
  createSnapshot: (req: {
    empId: number
    templateId?: number | null
    packageTotal: number
    effectiveDate?: string
    sourceType?: 'ONBOARD' | 'ADJUST' | 'MIGRATE'
    sourceRefId?: number
    remark?: string
  }) => request.post('/emp-salary-binding/snapshot', req),
  /**
   * V125-P8: 全员当前绑定分页列 (替代 P5 兜底方案).
   *
   * @param params.page       页码 (1 起, 默认 1)
   * @param params.size       每页大小 (默认 50)
   * @param params.keyword    工号 / 姓名模糊
   * @param params.templateId 按模板筛
   * @param params.hasBinding true=只看已绑定; false=只看未绑定
   */
  list: (params: {
    page?: number
    size?: number
    keyword?: string
    templateId?: number | null
    hasBinding?: boolean | null
  }) => request.get<any, EmpSalaryBindingListResp>('/emp-salary-binding/list', { params }),
}
